/**
 * metricsSections — Catálogo de secciones del hub de Métricas.
 *
 * QUÉ HACE: define las tarjetas que se muestran en `/metricas` (hub) y las
 *   pestañas del shell de cada sección. Cada sección tiene su sub-ruta real
 *   (`/metricas/<id>`), sin aparecer en el menú lateral.
 *
 * QUÉ EXPORTA: MetricsSectionId, METRICS_SECTIONS, getMetricsSection,
 *   isMetricsSectionId.
 * QUÉ NO DEBE HACER: no renderiza, no lee storage, no hace fetch.
 */
import {
  Clock,
  HeartPulse,
  LayoutDashboard,
  Package,
  Percent,
  TrendingUp,
  UserCheck,
  Users,
  type LucideIcon,
} from "lucide-react";

/** Identificadores estables de las secciones del hub. */
export type MetricsSectionId =
  | "resumen"
  | "negocio"
  | "resultados"
  | "rentabilidad"
  | "inventario"
  | "clientes"
  | "equipo"
  | "operacion";

interface MetricsSectionConfig {
  id: MetricsSectionId;
  /** Título de la tarjeta del hub y de la cabecera de la sección. */
  label: string;
  /** Texto corto de la pestaña en el shell. */
  menuLabel: string;
  /** Explicación de qué verá el usuario al abrir la sección. */
  description: string;
  /** Puntos clave de la sección (tarjeta del hub). */
  bullets: readonly string[];
  icon: LucideIcon;
  /** Color del icono (paleta de Métricas). */
  iconClass: string;
  /** Fondo suave del icono en la tarjeta del hub. */
  iconBgClass: string;
  /** Color del punto de cada viñeta en la tarjeta del hub. */
  dotClass: string;
}

/** Secciones del hub, en su orden de aparición. */
export const METRICS_SECTIONS: readonly MetricsSectionConfig[] = [
  {
    id: "resumen",
    label: "Resumen general",
    menuLabel: "Resumen",
    description:
      "Tu panel completo de KPIs y gráficas, con tarjetas que puedes mover y redimensionar.",
    bullets: [
      "Ventas, ticket promedio, hora pico y producto estrella",
      "Tendencia, métodos de pago, calendario y punto de equilibrio",
      "Personaliza qué indicadores ves y cómo se ordenan",
    ],
    icon: LayoutDashboard,
    iconClass: "text-blue-600 dark:text-blue-400",
    dotClass: "bg-blue-500",
    iconBgClass: "bg-blue-50 dark:bg-blue-950/40",
  },
  {
    id: "negocio",
    label: "Análisis del negocio",
    menuLabel: "Negocio",
    description:
      "Cómo está funcionando el negocio en general, qué está bien, qué está mal y dónde entrar a investigar.",
    bullets: [
      "Ventas del período, crecimiento, utilidad estimada y alertas activas",
      "Evolución del negocio, meta vs real, canal y categorías",
      "Concentración, calendario, día de la semana y salud del negocio",
    ],
    icon: HeartPulse,
    iconClass: "text-emerald-600 dark:text-emerald-400",
    dotClass: "bg-emerald-500",
    iconBgClass: "bg-emerald-50 dark:bg-emerald-950/40",
  },
  {
    id: "resultados",
    label: "Resultados",
    menuLabel: "Resultados",
    description:
      "Cuánto dinero genera el negocio y cómo evoluciona: ingresos, gastos, utilidad y flujo.",
    bullets: [
      "Ingresos, gastos, utilidad neta y flujo neto del período",
      "Cascada financiera, utilidad y flujo de caja",
      "Punto de equilibrio, ingresos por día, acumulado y pronóstico",
    ],
    icon: TrendingUp,
    iconClass: "text-sky-600 dark:text-sky-400",
    dotClass: "bg-sky-500",
    iconBgClass: "bg-sky-50 dark:bg-sky-950/40",
  },
  {
    id: "rentabilidad",
    label: "Rentabilidad",
    menuLabel: "Rentabilidad",
    description:
      "Dónde gana y dónde pierde margen el negocio: margen por producto, categoría y evolución.",
    bullets: [
      "Margen bruto, % margen, costo de ventas e ingresos",
      "Evolución del margen, margen peligroso e impacto de descuentos",
      "Ingresos vs costo por categoría y dispersión ventas × margen",
    ],
    icon: Percent,
    iconClass: "text-teal-600 dark:text-teal-400",
    dotClass: "bg-teal-500",
    iconBgClass: "bg-teal-50 dark:bg-teal-950/40",
  },
  {
    id: "inventario",
    label: "Inventario",
    menuLabel: "Inventario",
    description:
      "Dónde está el capital en productos: valor, rotación, cobertura, ABC y pérdidas.",
    bullets: [
      "Valor del inventario, rotación y stock crítico",
      "Productos estancados, mermas y clasificación ABC",
      "Cobertura de stock y capital inmovilizado por antigüedad",
    ],
    icon: Package,
    iconClass: "text-amber-600 dark:text-amber-400",
    dotClass: "bg-amber-500",
    iconBgClass: "bg-amber-50 dark:bg-amber-950/40",
  },
  {
    id: "clientes",
    label: "Clientes",
    menuLabel: "Clientes",
    description:
      "Quién compra y quién vuelve: recurrencia, frecuencia, ticket y clientes en riesgo.",
    bullets: [
      "Clientes nuevos vs recurrentes y cohortes de retención",
      "Frecuencia de compra, ticket promedio y top clientes",
      "Clientes inactivos, ventas por segmento y compra conjunta",
    ],
    icon: Users,
    iconClass: "text-violet-600 dark:text-violet-400",
    dotClass: "bg-violet-500",
    iconBgClass: "bg-violet-50 dark:bg-violet-950/40",
  },
  {
    id: "equipo",
    label: "Equipo",
    menuLabel: "Equipo",
    description:
      "Cómo funciona el equipo y cuánto aporta cada persona al negocio.",
    bullets: [
      "Rendimiento, productividad por hora y ticket por empleado",
      "Asistencia, puntualidad y descuentos otorgados",
      "Cumplimiento de tareas, carga de trabajo y ventas por vendedor",
    ],
    icon: UserCheck,
    iconClass: "text-rose-600 dark:text-rose-400",
    dotClass: "bg-rose-500",
    iconBgClass: "bg-rose-50 dark:bg-rose-950/40",
  },
  {
    id: "operacion",
    label: "Operación",
    menuLabel: "Operación",
    description:
      "Qué tan eficiente es el día a día: pedidos, tiempos, devoluciones y capacidad.",
    bullets: [
      "Mapa de calor hora × día y pedidos por estado",
      "Tiempo de preparación, atención y eficiencia de pedidos",
      "Demanda vs capacidad, devoluciones y ventas por turno",
    ],
    icon: Clock,
    iconClass: "text-cyan-600 dark:text-cyan-400",
    dotClass: "bg-cyan-500",
    iconBgClass: "bg-cyan-50 dark:bg-cyan-950/40",
  },
];

/** Busca una sección por id (undefined si no existe). */
export const getMetricsSection = (
  id: MetricsSectionId,
): MetricsSectionConfig | undefined =>
  METRICS_SECTIONS.find((section) => section.id === id);

/** Valida un id de sección contra el catálogo. */
export const isMetricsSectionId = (
  value: string | null,
): value is MetricsSectionId =>
  value !== null && METRICS_SECTIONS.some((section) => section.id === value);
